import { ClientEmail, ClientPhoneNumber } from '@provision/entity-types';
import { getFullName, getSiteName } from '@provision/formatting-utils';
import { Client, mapClient } from 'src/graphql/entities/client';
import { DeepPartial } from 'src/graphql/types';
import type { AddressRaw, ClientRaw } from 'src/types';

export function mapLabel(label?: string, labelId?: number) {
  if (!label && !labelId) {
    return null;
  }

  return {
    value: label,
    label,
    id: labelId,
  };
}

export function mapClientToAddress(client?: Partial<Client>) {
  if (!client) {
    return null;
  }

  return {
    ...client,
    emails: client.emails?.map(
      (email) =>
        ({
          ...email,
          value: email.email,
        }) as Partial<ClientEmail>,
    ),
    phoneNumbers: client.phoneNumbers?.map(
      (phone) =>
        ({
          ...phone,
          value: phone.number,
        }) as Partial<ClientPhoneNumber>,
    ),
    primaryEmail: client.primaryEmail
      ? ({
          id: client.primaryClientEmailId,
          value: client.primaryEmail,
          labelId: client.primaryEmailLabelId,
        } as Partial<ClientEmail>)
      : undefined,
    primaryPhoneNumber: client.primaryPhoneNumber
      ? ({
          id: client.primaryClientPhoneNumberId,
          value: parseInt(client.primaryPhoneNumber),
          labelId: client.primaryPhoneNumberLabelId,
        } as Partial<ClientPhoneNumber>)
      : undefined,
  } as DeepPartial<ClientRaw>;
}

export function mapAddress(address: AddressRaw) {
  const fullAddress = getSiteName(address);
  let primaryClient;
  let primaryProject;

  if (address.primaryClient) {
    primaryClient = mapClient(address.primaryClient);
  }

  if (address.projects?.length) {
    primaryProject = address.projects[0];
  }

  const addressSummary = address.addressSummaryRecordsViaAddressIdAddressId?.[0];
  const addressSubscription =
    address.addressSubscriptions?.find((sub) => sub.status !== 'canceled') ?? null;

  return {
    ...address,
    addressSummary,
    primaryProject,
    primaryClient,
    ownerFullName: getFullName(address.platformUser),
    fullAddress,
    name: fullAddress,
    address: [address.address1, address.address2].filter(Boolean).join(' '),
    id: address.addressId,
    addressId: address.addressId,
    partnerId: address.partnerId,
    city: address.city,
    state: address.state,
    address1: address.address1,
    address2: address.address2,
    zip: address.zip,
    businessName: address.businessName,
    orgNotes: address.orgNotes,
    clientActivatedMembership: address.clientActivatedMembership,
    platformSlaId: address.platformSlaId,
    primaryClientId: address.primaryClientId,
    ownerId: address.ownerId,
    brand: address.brand,
    brandId: address.brandId,
    cppExpDate: address.cppExpDate,
    creditCardProvided: address.creditCardProvided,
    labelAssignments: address.labelAssignments,
    clientSelectedMembership: address.clientSelectedMembership,
    chargifySubId: address.chargifySubId,
    tosAccepted: address.tosAccepted,
    membershipSalesNotes: address.membershipSalesNotes,
    projects: address.projects,
    addressSubscriptions: address.addressSubscriptions,
    addressSubscription,
    deletedAt: address.deletedAt,
    isDeleted: !!address.deletedAt,
  };
}
